/* oxlint-disable tailwindcss/migration-from-tailwind-2 -- `overflow-clip` at :113 IS a Tailwind v3 utility; the plugin misreads it as the v2 `text-clip`. Pre-existing, and unchanged by the move. */
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyH4, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { CalculatorResult } from '../../contracts';
import {
  formatInputLabel,
  getPrimaryValue,
  getScoreColorClass,
} from './calculatorConfig';
import { CollapsibleCalculation } from './CollapsibleCalculation';

import type { JSX } from 'react';

interface CalculatorBlockProps {
  calculator: string; // Calculator ID (e.g., "wells_dvt", "egfr_ckd_epi_2021")
  status: 'calculating' | 'completed' | 'error';
  result?: CalculatorResult;
  error?: string;
  className?: string;
}

/**
 * CalculatorBlock - Displays medical calculator results in the chat timeline
 *
 * Redesigned to match Figma "Calculator / Simple" component (EVI-730).
 *
 * Visual states:
 * - CALCULATING: Shows loading spinner with "Calculating..." text
 * - COMPLETED: Warm accent card with large score, interpretation, and collapsible calculation breakdown
 * - ERROR: Shows error message with alert icon
 */
export const CalculatorBlock = ({
  calculator,
  status,
  result,
  error,
  className,
}: CalculatorBlockProps): JSX.Element => {
  // Get display title - from result.calculator or format the calculator ID
  const title = result?.calculator ?? formatInputLabel(calculator);

  // Calculating state with spinner
  if (status === 'calculating') {
    return (
      <div
        data-testid="calculator-block"
        data-status="calculating"
        className={cn(
          'flex w-full items-center gap-3 rounded-xl bg-surface-accent p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-hover">
          <Loader2 className="size-5 animate-spin text-text-secondary" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <TypographyP2 className="text-text-tertiary">Calculating...</TypographyP2>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        data-testid="calculator-block"
        data-status="error"
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-validation-destructive-border bg-validation-destructive-muted p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        <FramedIcon size="md" state="error" icon={<AlertCircle />} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <TypographyP2 className="text-validation-destructive">
            {error ?? 'Calculation failed'}
          </TypographyP2>
        </div>
      </div>
    );
  }

  // Get primary value/score for display
  const primaryValue = result ? getPrimaryValue(result) : null;
  const scoreColorClass = getScoreColorClass(result?.risk_category);

  // Format score display value
  const formatScoreDisplay = (value: number): string => {
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  };

  // Determine if we have content for the collapsible section
  const hasEvaluatedTerms = result?.evaluated_terms && result.evaluated_terms.length > 0;
  const hasEvaluatedInputs =
    result?.evaluated_inputs && result.evaluated_inputs.length > 0;
  const hasConditionalityMap =
    result?.conditionality_map && result.conditionality_map.length > 0;
  const hasCollapsibleContent =
    hasEvaluatedTerms ||
    hasEvaluatedInputs ||
    hasConditionalityMap ||
    result?.formula ||
    result?.formula_human;

  // Completed state with result — Figma "Calculator / Simple" design
  return (
    <div
      data-testid="calculator-block"
      data-status="completed"
      className={cn(
        'flex w-full flex-col gap-6 overflow-clip rounded-xl bg-surface-accent p-6 duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Hero section */}
      <div className="flex flex-col gap-4 px-2">
        {/* Title */}
        <TypographyP2 className="font-medium text-text-secondary">{title}</TypographyP2>

        {/* Score display */}
        {primaryValue && (
          <div className="flex items-end gap-2">
            <span
              className={cn(
                'font-exposure text-5xl leading-none tracking-tight',
                scoreColorClass
              )}
            >
              {formatScoreDisplay(primaryValue.value)}
            </span>
            <span
              className={cn(
                'font-exposure text-2xl leading-tight tracking-tight',
                scoreColorClass
              )}
            >
              {primaryValue.unit}
            </span>
          </div>
        )}

        {/* Interpretation & Recommendation */}
        <div className="flex flex-col gap-1">
          {result?.interpretation && (
            <TypographyH4 className="text-text-primary">
              {result.interpretation}
            </TypographyH4>
          )}

          {result?.recommendation && (
            <TypographyP2 className="text-text-secondary">
              {result.recommendation}
            </TypographyP2>
          )}
        </div>
      </div>

      {/* Collapsible calculation breakdown */}
      {hasCollapsibleContent && (
        <CollapsibleCalculation
          formula={result?.formula}
          formulaHuman={result?.formula_human}
          formulaMachine={result?.formula_machine}
          evaluatedInputs={result?.evaluated_inputs}
          evaluatedTerms={result?.evaluated_terms}
          conditionalityMap={result?.conditionality_map}
          reference={result?.reference}
        />
      )}
    </div>
  );
};
